import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../models/api.model';
import { CarAvailability } from '../models/car.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class CarService {
  private apiUrl = `${environment.apiUrl}/catalog/car/`;
  private availabilityUrl = `${environment.apiUrl}/booking/car-availability/`;

  constructor(private http: HttpClient) {}

  list(params?: HttpParams): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(this.apiUrl, { params });
  }

  get(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}${id}/`);
  }

  /**
   * Legacy-эндпоинт со свободными авто на одну дату (`?date=YYYY-MM-DD`).
   * Используется `BookingCreateComponent.loadCars()` и другими потребителями
   * формы «одна дата». Не удалять — новые фичи используют `getFreeCars`.
   */
  getAvailability(date?: string): Observable<CarAvailability[]> {
    let params = new HttpParams();
    if (date) {
      params = params.set('date', date);
    }
    return this.http.get<CarAvailability[]>(this.availabilityUrl, { params });
  }

  /**
   * Свободные авто с фильтром по периоду. Каждый из параметров необязателен
   * и передаётся на backend только если он truthy (не `undefined`, не `''`):
   *
   * - `?start=A&end=B` → интервал `[A, B)`
   * - `?start=A`        → `[A, +∞)` (свободны начиная с A)
   * - `?end=B`          → `(-∞, B)` (свободны до B)
   * - (пусто)           → backend fallback на «сегодня»
   *
   * Формат значений: `YYYY-MM-DDTHH:MM` (без tz, без секунд).
   * Валидацию `end > start` делает фронт (`rangeValidator` в `CarListComponent`),
   * backend при обоих заполненных также проверяет.
   */
  getFreeCars(start?: string, end?: string): Observable<CarAvailability[]> {
    let params = new HttpParams();
    if (start) {
      params = params.set('start', start);
    }
    if (end) {
      params = params.set('end', end);
    }
    return this.http.get<CarAvailability[]>(this.availabilityUrl, { params });
  }

  getCarAvailability(id: number, date?: string): Observable<CarAvailability> {
    let params = new HttpParams();
    if (date) {
      params = params.set('date', date);
    }
    return this.http.get<CarAvailability>(`${this.availabilityUrl}${id}/`, { params });
  }

  /**
   * Доступность конкретного авто с фильтром по периоду. Семантика параметров
   * та же, что у `getFreeCars`: каждый передаётся только если truthy.
   */
  getCarFreeInRange(id: number, start?: string, end?: string): Observable<CarAvailability> {
    let params = new HttpParams();
    if (start) {
      params = params.set('start', start);
    }
    if (end) {
      params = params.set('end', end);
    }
    return this.http.get<CarAvailability>(`${this.availabilityUrl}${id}/`, { params });
  }
}